import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Clock3Icon,
  EyeIcon,
  GaugeIcon,
  Loader2Icon,
  RefreshCwIcon,
  StarIcon,
  TargetIcon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const LOGO_URL =
  "https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png";

const POLL_INTERVAL_MS = 10000;

const scoreKey = (unitId, holeNumber) =>
  `${unitId}-${holeNumber}`;

const formatRelativeToPar = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return "–";
  if (number === 0) return "E";
  return number > 0 ? `+${number}` : String(number);
};

const formatDateTime = (value) => {
  if (!value) return "–";

  return new Intl.DateTimeFormat("sv-SE", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const formatDistance = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "–";

  return `${number.toLocaleString("sv-SE", {
    maximumFractionDigits: 2,
  })} m`;
};

const getHoleResultLabel = (strokes, par) => {
  if (
    strokes == null ||
    strokes === "" ||
    par == null ||
    par === ""
  ) {
    return "Ej spelat";
  }

  const score = Number(strokes);
  const holePar = Number(par);

  if (!Number.isFinite(score) || !Number.isFinite(holePar)) {
    return "Ej spelat";
  }

  const difference = score - holePar;
  if (difference <= -3) return "Albatross eller bättre";
  if (difference === -2) return "Eagle";
  if (difference === -1) return "Birdie";
  if (difference === 0) return "Par";
  if (difference === 1) return "Bogey";
  return "Dubbelbogey eller sämre";
};

const getHoleScoreMarkerClass = (strokes, par) => {
  if (
    strokes == null ||
    strokes === "" ||
    par == null ||
    par === ""
  ) {
    return "text-[#9aa09b]";
  }

  const score = Number(strokes);
  const holePar = Number(par);

  if (!Number.isFinite(score) || !Number.isFinite(holePar)) {
    return "text-[#9aa09b]";
  }

  const difference = score - holePar;

  if (difference <= -2) {
    return "rounded-full ring-2 ring-[#d6ad35] text-[#7a5a0a]";
  }

  if (difference === -1) {
    return "rounded-full ring-2 ring-[#bd4d49] text-[#9f3532]";
  }

  if (difference === 1) {
    return "rounded-[4px] bg-[#dcecf5] text-[#24465d]";
  }

  if (difference >= 2) {
    return "rounded-[4px] bg-[#315b79] text-white";
  }

  return "text-[#1f2d26]";
};

const normalizePayload = (value, previousPlayerStats = {}) => ({
  tournament: value?.tournament || {},
  round: value?.round || {},
  liveRound: value?.liveRound || {},
  balls: Array.isArray(value?.balls) ? value.balls : [],
  ballUnits: Array.isArray(value?.ballUnits)
    ? value.ballUnits
    : [],
  units: Array.isArray(value?.units) ? value.units : [],
  scores: Array.isArray(value?.scores) ? value.scores : [],
  miniGameHoles: Array.isArray(value?.miniGameHoles)
    ? value.miniGameHoles
    : [],
  miniGameAwards: Array.isArray(value?.miniGameAwards)
    ? value.miniGameAwards
    : [],
  previousPlayerStats:
    previousPlayerStats && typeof previousPlayerStats === "object"
      ? previousPlayerStats
      : {},
});

export default function LiveSpectator() {
  const { token } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fatalError, setFatalError] = useState("");
  const [pageMessage, setPageMessage] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [leaderboardScope, setLeaderboardScope] =
    useState("round");
  const [includeMiniGamesInRanking, setIncludeMiniGamesInRanking] =
    useState(false);
  const [expandedLeaderboardUnitId, setExpandedLeaderboardUnitId] =
    useState(null);

  const fetchLiveRound = useCallback(
    async ({ initial = false } = {}) => {
      if (!token) {
        setFatalError("Tittarlänken saknas.");
        setLoading(false);
        return;
      }

      if (initial) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const [liveResponse, previousResponse] = await Promise.all([
          supabase.rpc("get_public_live_round_by_token", {
            p_token: token,
          }),
          supabase.rpc(
            "get_public_live_previous_player_stats_by_token",
            { p_token: token }
          ),
        ]);

        if (liveResponse.error) {
          throw liveResponse.error;
        }

        if (!liveResponse.data?.success) {
          throw new Error(
            liveResponse.data?.error ||
              "Live-rundan kunde inte hämtas."
          );
        }

        let previousPlayerStats = {};

        if (
          !previousResponse.error &&
          previousResponse.data?.success
        ) {
          previousPlayerStats =
            previousResponse.data.stats || {};
        } else if (previousResponse.error) {
          console.warn(
            "Tidigare totaler kunde inte hämtas:",
            previousResponse.error
          );
        }

        setData(
          normalizePayload(
            liveResponse.data,
            previousPlayerStats
          )
        );
        setFatalError("");
        setPageMessage("");
        setLastUpdated(new Date());
      } catch (error) {
        console.error(
          "Kunde inte hämta publik livevisning:",
          error
        );

        if (initial) {
          setFatalError(
            error?.message ||
              "Live-rundan kunde inte hämtas."
          );
        } else {
          setPageMessage(
            "Den senaste uppdateringen misslyckades. Visar senast hämtade resultat."
          );
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchLiveRound({ initial: true });
  }, [fetchLiveRound]);

  useEffect(() => {
    if (!token || fatalError) return undefined;

    const intervalId = window.setInterval(() => {
      fetchLiveRound();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [fatalError, fetchLiveRound, token]);

  useEffect(() => {
    if (leaderboardScope !== "round") {
      setExpandedLeaderboardUnitId(null);
    }
  }, [leaderboardScope]);

  const holes = Number(
    data?.liveRound?.holes || data?.round?.holes || 18
  );

  const holePars = useMemo(() => {
    const values = data?.liveRound?.hole_pars;

    if (!Array.isArray(values) || values.length !== holes) {
      return null;
    }

    const normalized = values.map((value) => Number(value));
    const valid = normalized.every(
      (value) =>
        Number.isInteger(value) && value >= 2 && value <= 7
    );

    return valid ? normalized : null;
  }, [data?.liveRound?.hole_pars, holes]);

  const scoreMap = useMemo(() => {
    const map = {};

    (data?.scores || []).forEach((score) => {
      map[scoreKey(score.scoring_unit_id, score.hole_number)] =
        Number(score.strokes);
    });

    return map;
  }, [data?.scores]);

  const unitBallMap = useMemo(() => {
    const map = {};

    (data?.ballUnits || []).forEach((relation) => {
      map[relation.scoring_unit_id] = relation.live_ball_id;
    });

    return map;
  }, [data?.ballUnits]);

  const ballNumberMap = useMemo(() => {
    const map = {};

    (data?.balls || []).forEach((ball) => {
      map[ball.id] = ball.ball_number;
    });

    return map;
  }, [data?.balls]);

  const miniGameConfigMap = useMemo(
    () =>
      Object.fromEntries(
        (data?.miniGameHoles || []).map((config) => [
          config.id,
          config,
        ])
      ),
    [data?.miniGameHoles]
  );

  const playerNameMap = useMemo(() => {
    const map = {};

    (data?.units || []).forEach((unit) => {
      Object.entries(unit.player_names || {}).forEach(
        ([playerId, playerName]) => {
          map[String(playerId)] = playerName;
        }
      );

      if (
        unit.unit_type === "player" &&
        unit.player_ids?.[0]
      ) {
        map[String(unit.player_ids[0])] = unit.label;
      }
    });

    return map;
  }, [data?.units]);

  const leaderboardRows = useMemo(() => {
    const units = data?.units || [];
    const scores = data?.scores || [];
    const miniGameAwards = data?.miniGameAwards || [];
    const previousPlayerStats =
      data?.previousPlayerStats || {};

    const anyHandicapDeductions = units.some(
      (unit) => Number(unit.round_deduction) > 0
    );
    const competitionTeamData =
      data?.liveRound?.team_data || {};
    const competitionTeamAssignments =
      competitionTeamData.assignments || {};

    const getCompetitionTeam = (playerIds = []) => {
      if (
        !data?.liveRound?.team_mode ||
        Object.keys(competitionTeamAssignments).length === 0
      ) {
        return null;
      }

      const teamKeys = [
        ...new Set(
          playerIds
            .map(
              (playerId) =>
                competitionTeamAssignments[String(playerId)] ??
                competitionTeamAssignments[playerId]
            )
            .filter(Boolean)
        ),
      ];

      if (teamKeys.length !== 1) return null;

      const teamKey = teamKeys[0];

      return {
        key: teamKey,
        name:
          competitionTeamData.captains?.[teamKey] ||
          (teamKey === "red" ? "Röd" : "Grön"),
        color:
          competitionTeamData.colors?.[teamKey] ||
          (teamKey === "red" ? "#9F3A38" : "#2F6B4F"),
      };
    };

    const getAwardDeduction = (awards) =>
      (awards || []).reduce((total, award) => {
        const config =
          miniGameConfigMap[
            award.live_mini_game_hole_id
          ];

        return (
          total +
          (config?.subtract_from_score
            ? Number(config.deduction_value || 0)
            : 0)
        );
      }, 0);

    const roundRows = units.map((unit) => {
      const unitScores = scores.filter(
        (score) => score.scoring_unit_id === unit.id
      );
      const gross = unitScores.reduce(
        (sum, score) => sum + Number(score.strokes || 0),
        0
      );
      const holesPlayed = new Set(
        unitScores
          .filter((score) => Number(score.strokes) > 0)
          .map((score) => Number(score.hole_number))
      ).size;
      const handicapDeduction = anyHandicapDeductions
        ? Number(unit.round_deduction || 0)
        : 0;
      const unitAwards = miniGameAwards.filter((award) =>
        (unit.player_ids || []).some(
          (playerId) =>
            String(playerId) === String(award.player_id)
        )
      );
      const miniGameDeduction =
        getAwardDeduction(unitAwards);
      const appliedMiniGameDeduction =
        includeMiniGamesInRanking &&
        unit.unit_type !== "team"
          ? miniGameDeduction
          : 0;
      const starCount = unitAwards.length;
      const baseRoundScore = gross - handicapDeduction;
      const roundScore =
        baseRoundScore - appliedMiniGameDeduction;
      const holeScores = Array.from(
        { length: holes },
        (_, index) =>
          scoreMap[scoreKey(unit.id, index + 1)] || null
      );
      const parPlayed = holePars
        ? unitScores.reduce((sum, score) => {
            const par = Number(
              holePars[Number(score.hole_number) - 1]
            );

            return sum + (Number.isFinite(par) ? par : 0);
          }, 0)
        : null;
      const relativeToPar = holePars
        ? gross -
          Number(parPlayed || 0) -
          handicapDeduction -
          appliedMiniGameDeduction
        : null;
      const roundSortScore =
        relativeToPar == null ? roundScore : relativeToPar;

      return {
        ...unit,
        ballNumber:
          ballNumberMap[unitBallMap[unit.id]] || "–",
        gross,
        holesPlayed,
        baseRoundScore,
        roundScore,
        holeScores,
        parPlayed,
        relativeToPar,
        roundSortScore,
        displayScore:
          relativeToPar == null
            ? roundScore
            : formatRelativeToPar(relativeToPar),
        handicapDeduction,
        miniGameDeduction,
        appliedMiniGameDeduction,
        starCount,
        unitAwards,
        competitionTeam: getCompetitionTeam(
          unit.player_ids || []
        ),
      };
    });

    roundRows.sort((a, b) => {
      const aScore = Number.isFinite(a.roundSortScore)
        ? a.roundSortScore
        : Number.POSITIVE_INFINITY;
      const bScore = Number.isFinite(b.roundSortScore)
        ? b.roundSortScore
        : Number.POSITIVE_INFINITY;

      if (aScore !== bScore) return aScore - bScore;

      const sortOrderDifference =
        Number(a.sort_order || 0) -
        Number(b.sort_order || 0);

      if (sortOrderDifference !== 0) {
        return sortOrderDifference;
      }

      return String(a.label || "").localeCompare(
        String(b.label || ""),
        "sv-SE"
      );
    });

    const totalRowsByPlayer = new Map();

    roundRows.forEach((row) => {
      (row.player_ids || []).forEach((playerId) => {
        const playerKey = String(playerId);
        const currentAwards = row.unitAwards.filter(
          (award) =>
            String(award.player_id) === playerKey
        );
        const currentMiniGameDeduction =
          getAwardDeduction(currentAwards);
        const currentRoundScore =
          row.baseRoundScore - currentMiniGameDeduction;
        const fetchedPreviousStats =
          previousPlayerStats?.[playerKey];
        const fallbackPreviousTotal = Number(
          row.player_previous_totals?.[playerKey] ??
            row.player_previous_totals?.[playerId] ??
            row.previous_total ??
            0
        );
        const previousTotal =
          fetchedPreviousStats &&
          typeof fetchedPreviousStats === "object"
            ? Number(
                fetchedPreviousStats.total ??
                  fallbackPreviousTotal
              )
            : fallbackPreviousTotal;
        const previousMiniGameDeduction =
          fetchedPreviousStats &&
          typeof fetchedPreviousStats === "object"
            ? Number(
                fetchedPreviousStats.mini_game_deduction || 0
              )
            : 0;
        const previousStarCount =
          fetchedPreviousStats &&
          typeof fetchedPreviousStats === "object"
            ? Number(fetchedPreviousStats.star_count || 0)
            : 0;
        const totalScore = previousTotal + currentRoundScore;
        const playerName =
          row.player_names?.[playerKey] ??
          row.player_names?.[playerId] ??
          (row.unit_type === "player"
            ? row.label
            : "Okänd spelare");

        totalRowsByPlayer.set(playerKey, {
          ...row,
          id: `total-player-${playerKey}`,
          sourceUnitId: row.id,
          unit_type: "player_total",
          label: playerName,
          player_ids: [playerId],
          previousTotal,
          currentRoundScore,
          totalScore,
          displayScore: totalScore,
          miniGameDeduction:
            previousMiniGameDeduction +
            currentMiniGameDeduction,
          appliedMiniGameDeduction:
            currentMiniGameDeduction,
          starCount:
            previousStarCount + currentAwards.length,
          currentRoundStarCount: currentAwards.length,
          unitAwards: currentAwards,
          competitionTeam: getCompetitionTeam([playerId]),
        });
      });
    });

    const totalRows = [
      ...totalRowsByPlayer.values(),
    ].sort((a, b) => {
      if (a.totalScore !== b.totalScore) {
        return a.totalScore - b.totalScore;
      }

      return String(a.label || "").localeCompare(
        String(b.label || ""),
        "sv-SE"
      );
    });

    const rows =
      leaderboardScope === "total"
        ? totalRows
        : roundRows;
    const roundHasIndividualUnits = roundRows.some(
      (row) => row.unit_type === "player"
    );
    const hasMiniGameDeductions = (
      data?.miniGameHoles || []
    ).some((config) => config.subtract_from_score);
    const roundAnyDeductions =
      anyHandicapDeductions ||
      (includeMiniGamesInRanking &&
        roundHasIndividualUnits &&
        hasMiniGameDeductions);
    const totalAnyDeductions =
      anyHandicapDeductions ||
      totalRows.some((row) => row.miniGameDeduction > 0);

    return {
      rows,
      roundRows,
      totalRows,
      hasPars: Boolean(holePars),
      hasMiniGameDeductions,
      hasIndividualUnits: roundHasIndividualUnits,
      anyDeductions:
        leaderboardScope === "total"
          ? totalAnyDeductions
          : roundAnyDeductions,
    };
  }, [
    ballNumberMap,
    data?.liveRound?.team_data,
    data?.liveRound?.team_mode,
    data?.miniGameAwards,
    data?.miniGameHoles,
    data?.previousPlayerStats,
    data?.scores,
    data?.units,
    holePars,
    holes,
    includeMiniGamesInRanking,
    leaderboardScope,
    miniGameConfigMap,
    scoreMap,
    unitBallMap,
  ]);

  const teamLeaderboardRows = useMemo(() => {
    const teamData = data?.liveRound?.team_data || {};
    const assignments = teamData.assignments || {};

    if (
      !data?.liveRound?.team_mode ||
      Object.keys(assignments).length === 0
    ) {
      return [];
    }

    const configuredKeys = ["red", "green"].filter(
      (teamKey) =>
        Object.values(assignments).includes(teamKey)
    );

    return configuredKeys
      .map((teamKey) => {
        let roundTotal = 0;
        let previousTotal = 0;
        let miniGameDeduction = 0;
        const playerIds = new Set();
        let holesPlayed = 0;

        leaderboardRows.roundRows.forEach((row) => {
          const teamPlayerIds = (row.player_ids || []).filter(
            (playerId) =>
              (assignments[String(playerId)] ??
                assignments[playerId]) === teamKey
          );

          if (teamPlayerIds.length === 0) return;

          holesPlayed = Math.max(
            holesPlayed,
            row.holesPlayed
          );

          teamPlayerIds.forEach((playerId) => {
            playerIds.add(String(playerId));

            const playerStarDeduction = (
              data?.miniGameAwards || []
            ).reduce((total, award) => {
              if (
                String(award.player_id) !==
                String(playerId)
              ) {
                return total;
              }

              const config =
                miniGameConfigMap[
                  award.live_mini_game_hole_id
                ];

              return (
                total +
                (config?.subtract_from_score
                  ? Number(config.deduction_value || 0)
                  : 0)
              );
            }, 0);

            roundTotal +=
              row.baseRoundScore - playerStarDeduction;
            miniGameDeduction += playerStarDeduction;

            const previousStats =
              data?.previousPlayerStats?.[String(playerId)];

            previousTotal += Number(
              previousStats &&
                typeof previousStats === "object"
                ? previousStats.total ??
                    row.player_previous_totals?.[
                      String(playerId)
                    ] ??
                    row.player_previous_totals?.[playerId] ??
                    row.previous_total ??
                    0
                : row.player_previous_totals?.[
                    String(playerId)
                  ] ??
                    row.player_previous_totals?.[playerId] ??
                    row.previous_total ??
                    0
            );
          });
        });

        return {
          key: teamKey,
          name:
            teamData.captains?.[teamKey] ||
            (teamKey === "red" ? "Röd" : "Grön"),
          color:
            teamData.colors?.[teamKey] ||
            (teamKey === "red" ? "#9F3A38" : "#2F6B4F"),
          playerCount: playerIds.size,
          holesPlayed,
          roundTotal,
          miniGameDeduction,
          total: previousTotal + roundTotal,
        };
      })
      .sort((a, b) => {
        const aScore =
          leaderboardScope === "total"
            ? a.total
            : a.roundTotal;
        const bScore =
          leaderboardScope === "total"
            ? b.total
            : b.roundTotal;

        return aScore - bScore;
      });
  }, [
    data?.liveRound?.team_data,
    data?.liveRound?.team_mode,
    data?.miniGameAwards,
    data?.previousPlayerStats,
    leaderboardRows.roundRows,
    leaderboardScope,
    miniGameConfigMap,
  ]);

  const miniGameResults = useMemo(
    () =>
      (data?.miniGameHoles || []).map((config) => {
        const award = (data?.miniGameAwards || []).find(
          (item) =>
            item.live_mini_game_hole_id === config.id
        );

        return {
          ...config,
          award,
          winnerName: award
            ? playerNameMap[String(award.player_id)] ||
              "Okänd spelare"
            : null,
        };
      }),
    [
      data?.miniGameAwards,
      data?.miniGameHoles,
      playerNameMap,
    ]
  );

  const completedHoles = useMemo(() => {
    if (!leaderboardRows.roundRows.length) return 0;

    return Math.max(
      ...leaderboardRows.roundRows.map(
        (row) => row.holesPlayed
      ),
      0
    );
  }, [leaderboardRows.roundRows]);

  const renderCompactScorecard = (holeScores) => {
    const segments = [
      {
        key: "front",
        label:
          holeScores.length > 9 ? "Front 9" : "Hål 1–9",
        start: 0,
        end: Math.min(9, holeScores.length),
      },
      ...(holeScores.length > 9
        ? [
            {
              key: "back",
              label: "Back 9",
              start: 9,
              end: holeScores.length,
            },
          ]
        : []),
    ];

    return (
      <div className="space-y-3">
        {segments.map((segment) => {
          const segmentScores = holeScores.slice(
            segment.start,
            segment.end
          );
          const segmentPars =
            holePars?.slice(segment.start, segment.end) || null;
          const segmentGross = segmentScores.reduce(
            (sum, score) => sum + Number(score || 0),
            0
          );
          const played = segmentScores.filter(
            (score) => score != null
          ).length;

          return (
            <div key={segment.key}>
              <div className="mb-1.5 flex items-center justify-between px-0.5">
                <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#68716c]">
                  {segment.label}
                </span>
                {played > 0 && (
                  <span className="text-[9px] font-medium tabular-nums text-[#68716c]">
                    {segmentGross} slag
                  </span>
                )}
              </div>

              <div className="overflow-hidden rounded-lg border border-[#d8d2c4]">
                <table className="w-full table-fixed border-collapse text-[9px] sm:text-[10px]">
                  <colgroup>
                    <col style={{ width: "34px" }} />
                    {segmentScores.map((_, index) => (
                      <col key={index} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr className="bg-[#183d2d] text-white">
                      <th className="px-0.5 py-2 text-center text-[8px] font-semibold uppercase tracking-[0.08em]">
                        Hål
                      </th>
                      {segmentScores.map((_, index) => (
                        <th
                          key={segment.start + index + 1}
                          className="border-l border-white/10 px-0.5 py-2 text-center font-semibold"
                        >
                          {segment.start + index + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {segmentPars && (
                      <tr className="border-b border-[#e4ded1] bg-[#f7f4ec]">
                        <th className="px-0.5 py-1.5 text-center text-[8px] font-semibold uppercase tracking-[0.08em] text-[#68716c]">
                          Par
                        </th>
                        {segmentPars.map((par, index) => (
                          <td
                            key={index}
                            className="border-l border-[#e4ded1] px-0.5 py-1.5 text-center font-medium tabular-nums text-[#68716c]"
                          >
                            {par}
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr className="bg-[#fffdf8]">
                      <th className="px-0.5 py-2 text-center text-[8px] font-semibold uppercase tracking-[0.08em] text-[#68716c]">
                        Slag
                      </th>
                      {segmentScores.map((score, index) => {
                        const absoluteIndex =
                          segment.start + index;
                        const par = holePars?.[absoluteIndex];

                        return (
                          <td
                            key={absoluteIndex}
                            className="border-l border-[#e4ded1] px-0.5 py-2 text-center"
                            title={getHoleResultLabel(
                              score,
                              par
                            )}
                          >
                            <span
                              className={`inline-flex h-5 min-w-5 items-center justify-center px-0.5 text-[10px] font-semibold tabular-nums ${getHoleScoreMarkerClass(
                                score,
                                par
                              )}`}
                            >
                              {score ?? "–"}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 text-[#1f2d26] shadow-sm">
          <Loader2Icon className="h-5 w-5 animate-spin text-[#183d2d]" />
          <div>
            <p className="text-sm font-semibold">
              Öppnar livevisningen
            </p>
            <p className="text-xs text-[#68716c]">
              Hämtar senaste resultat…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (fatalError && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="w-full max-w-md rounded-[24px] border border-[#dfc5c0] bg-[#fffdf8] p-6 text-center shadow-sm">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#f9e9e6] text-[#a0443e]">
            <AlertCircleIcon className="h-6 w-6" />
          </span>
          <h1 className="mt-4 text-xl font-semibold text-[#183d2d]">
            Livevisningen är inte tillgänglig
          </h1>
          <p className="mt-2 text-sm leading-6 text-[#68716c]">
            {fatalError}
          </p>
          <button
            type="button"
            onClick={() => fetchLiveRound({ initial: true })}
            className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#214d39]"
          >
            <RefreshCwIcon className="h-4 w-4" />
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  const isCompleted =
    data?.liveRound?.status === "completed";
  const hasMultipleRounds =
    Number(data?.liveRound?.round_count || 1) > 1;

  return (
    <div className="min-h-screen bg-[#f3f0e7] pb-12 text-[#1f2d26]">
      <header className="relative overflow-hidden border-b border-black/10 bg-[#183d2d] text-[#fffdf8]">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 h-60 w-60 rounded-full bg-white/10 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 pb-6 pt-5 sm:px-6 sm:pb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                <img
                  src={LOGO_URL}
                  alt="HoleInOne"
                  className="h-7 w-7 object-contain brightness-125"
                />
              </span>
              <div className="min-w-0">
                <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/50">
                  HoleInOne.se · Live
                </p>
                <h1 className="mt-1 truncate text-xl font-semibold sm:text-2xl">
                  {data?.tournament?.name || "Golftävling"}
                </h1>
                <p className="mt-1 truncate text-sm text-white/65">
                  {data?.round?.name || "Live-runda"}
                </p>
              </div>
            </div>

            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/75">
              <EyeIcon className="h-3.5 w-3.5" />
              Endast visning
            </span>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                  isCompleted
                    ? "bg-[#d8c38d]/15 text-[#e0cd98]"
                    : "bg-[#4da476]/18 text-[#9ee0b8]"
                }`}
              >
                {isCompleted ? "Avslutad" : "Live"}
              </span>
              <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[10px] font-medium text-white/75">
                {completedHoles}/{holes} hål
              </span>
              {data?.liveRound?.started_at && (
                <span className="text-[10px] text-white/50">
                  Startad {formatDateTime(data.liveRound.started_at)}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => fetchLiveRound()}
              disabled={refreshing}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/15 disabled:opacity-50"
            >
              <RefreshCwIcon
                className={`h-4 w-4 ${
                  refreshing ? "animate-spin" : ""
                }`}
              />
              {refreshing ? "Uppdaterar…" : "Uppdatera"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-3 sm:px-6">
        <div className="relative z-10 -mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-4 py-3 text-xs text-[#68716c] shadow-[0_8px_24px_rgba(31,45,38,0.10)]">
          <span className="inline-flex items-center gap-2">
            <Clock3Icon className="h-4 w-4 text-[#9a7735]" />
            Uppdateras automatiskt var tionde sekund
          </span>
          <span>
            Senast uppdaterad{" "}
            {lastUpdated?.toLocaleTimeString("sv-SE", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            }) || "–"}
          </span>
        </div>

        {pageMessage && (
          <div
            className="mt-4 flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
            role="status"
          >
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="leading-5">{pageMessage}</p>
          </div>
        )}

        <section className="mt-4 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
          <div className="border-b border-[#d8d2c4] px-4 py-4 sm:px-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#68716c]">
                  Live från alla bollar
                </p>
                <h2 className="mt-1 text-lg font-semibold text-[#183d2d]">
                  Live-ställning
                </h2>
              </div>

              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                <TrophyIcon className="h-4 w-4" />
              </span>
            </div>

            <p className="mt-2 text-xs leading-5 text-[#68716c]">
              Tryck på en spelare eller ett scramblelag under Pågående runda för att öppna hela scorekortet. Totalvyn visar tävlingens tidigare resultat tillsammans med den pågående rundan.
            </p>

            {hasMultipleRounds && (
              <div className="mt-3 grid grid-cols-2 gap-1 rounded-xl bg-[#f7f4ec] p-1">
                <button
                  type="button"
                  onClick={() => setLeaderboardScope("round")}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                    leaderboardScope === "round"
                      ? "bg-[#183d2d] text-white shadow-sm"
                      : "text-[#68716c]"
                  }`}
                >
                  Pågående runda
                </button>
                <button
                  type="button"
                  onClick={() => setLeaderboardScope("total")}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                    leaderboardScope === "total"
                      ? "bg-[#183d2d] text-white shadow-sm"
                      : "text-[#68716c]"
                  }`}
                >
                  Totalt
                </button>
              </div>
            )}

            {leaderboardScope === "round" &&
              leaderboardRows.hasMiniGameDeductions &&
              leaderboardRows.hasIndividualUnits && (
                <div className="mt-3 flex items-center justify-end gap-2 text-[10px] text-[#68716c]">
                  <span>Stjärnor i individuell ranking</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={includeMiniGamesInRanking}
                    onClick={() =>
                      setIncludeMiniGamesInRanking(
                        (current) => !current
                      )
                    }
                    className={`relative h-5 w-9 shrink-0 rounded-full border transition ${
                      includeMiniGamesInRanking
                        ? "border-[#183d2d] bg-[#183d2d]"
                        : "border-[#c8c1b3] bg-[#e8e4da]"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                        includeMiniGamesInRanking
                          ? "translate-x-[18px]"
                          : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              )}
          </div>

          <div className="divide-y divide-[#e4ded1]">
            {leaderboardRows.rows.map((row, index) => {
              const isExpanded =
                leaderboardScope === "round" &&
                expandedLeaderboardUnitId === row.id;
              const expandedMiniGameWins = (
                row.unitAwards || []
              )
                .map((award) => {
                  const config =
                    miniGameConfigMap[
                      award.live_mini_game_hole_id
                    ];

                  if (!config) return null;

                  const playerKey = String(award.player_id);
                  const winnerName =
                    row.player_names?.[playerKey] ??
                    row.player_names?.[award.player_id] ??
                    (row.unit_type === "player"
                      ? row.label
                      : "Okänd spelare");

                  return {
                    id:
                      award.id ||
                      `${award.live_mini_game_hole_id}-${playerKey}`,
                    winnerName,
                    gameName:
                      config.mini_game_name || "Sidotävling",
                    holeNumber: Number(config.hole_number),
                    deduction: config.subtract_from_score
                      ? Number(config.deduction_value || 0)
                      : 0,
                  };
                })
                .filter(Boolean)
                .sort(
                  (a, b) =>
                    a.holeNumber - b.holeNumber ||
                    a.gameName.localeCompare(
                      b.gameName,
                      "sv-SE"
                    )
                );

              return (
                <div
                  key={row.id}
                  className={
                    index % 2 === 0
                      ? "bg-[#fffdf8]"
                      : "bg-[#f7f4ec]"
                  }
                  style={
                    row.competitionTeam
                      ? {
                          boxShadow: `inset 3px 0 0 ${row.competitionTeam.color}`,
                        }
                      : undefined
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (leaderboardScope !== "round") return;

                      setExpandedLeaderboardUnitId(
                        (current) =>
                          current === row.id ? null : row.id
                      );
                    }}
                    className={`grid w-full items-center gap-2 px-2.5 text-left sm:gap-3 sm:px-5 ${
                      leaderboardScope === "round"
                        ? "grid-cols-[34px_minmax(0,1fr)_48px_18px] cursor-pointer py-3.5 transition hover:bg-black/[0.025] sm:grid-cols-[38px_minmax(0,1fr)_64px_20px]"
                        : "grid-cols-[34px_minmax(0,1fr)_76px] py-3 sm:grid-cols-[38px_minmax(0,1fr)_86px]"
                    }`}
                    aria-expanded={isExpanded}
                  >
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                        index === 0
                          ? "bg-[#183d2d] text-white"
                          : "border border-[#d8d2c4] bg-[#fffdf8] text-[#68716c]"
                      }`}
                    >
                      {index + 1}
                    </span>

                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-semibold text-[#1f2d26]">
                        <span className="min-w-0 break-words leading-snug">
                          {row.label}
                        </span>
                        {row.starCount > 0 && (
                          <span
                            className="inline-flex shrink-0 items-center gap-px text-[#b08b45]"
                            title={`${row.starCount} vinster i sidotävlingar`}
                          >
                            {Array.from(
                              {
                                length: Math.min(
                                  row.starCount,
                                  4
                                ),
                              },
                              (_, starIndex) => (
                                <StarIcon
                                  key={starIndex}
                                  className="h-3.5 w-3.5 fill-current"
                                />
                              )
                            )}
                            {row.starCount > 4 && (
                              <span className="ml-0.5 text-[9px] font-semibold">
                                +{row.starCount - 4}
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#68716c]">
                        {leaderboardScope === "total" ? (
                          <>
                            <span>
                              Före rundan {row.previousTotal}
                            </span>
                            <span>
                              Pågående {row.currentRoundScore}
                            </span>
                          </>
                        ) : (
                          <>
                            <span>
                              {row.holesPlayed}/{holes} hål
                            </span>
                            {leaderboardRows.hasPars &&
                              row.holesPlayed > 0 && (
                                <span>· Brutto {row.gross}</span>
                              )}
                            <span>· Boll {row.ballNumber}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="min-w-0 text-right">
                      <p className="text-xl font-semibold leading-none tabular-nums text-[#183d2d]">
                        {row.displayScore}
                      </p>
                      {(leaderboardScope === "total" ||
                        !leaderboardRows.hasPars) && (
                        <p className="mt-1 whitespace-nowrap text-[8px] font-medium uppercase tracking-[0.1em] text-[#68716c]">
                          {leaderboardScope === "total"
                            ? "Inkl. live"
                            : leaderboardRows.anyDeductions
                              ? "Prel. netto"
                              : "Slag"}
                        </p>
                      )}
                      {row.miniGameDeduction > 0 && (
                        <p className="mt-0.5 whitespace-nowrap text-[9px] font-semibold text-[#9a7735]">
                          {leaderboardScope === "total"
                            ? `★ −${row.miniGameDeduction}`
                            : row.unit_type === "team"
                              ? ""
                              : includeMiniGamesInRanking
                                ? `★ −${row.miniGameDeduction}`
                                : `★ −${row.miniGameDeduction} ej inräknat`}
                        </p>
                      )}
                    </div>

                    {leaderboardScope === "round" && (
                      <span className="flex w-[18px] justify-end sm:w-5">
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4 shrink-0 text-[#9a7735]" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#9a7735]" />
                        )}
                      </span>
                    )}
                  </button>

                  {isExpanded && (
                    <div className="border-t border-[#d8d2c4] bg-[#fffdf8] px-3 py-4 sm:px-5">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#68716c]">
                            Fullständigt scorekort
                          </p>
                          <h3 className="mt-1 text-sm font-semibold text-[#183d2d]">
                            {row.label}
                          </h3>
                        </div>
                        {leaderboardRows.hasPars &&
                          row.holesPlayed > 0 && (
                            <span className="rounded-full bg-[#e5ede7] px-3 py-1.5 text-xs font-semibold text-[#183d2d]">
                              {formatRelativeToPar(
                                row.relativeToPar
                              )}
                            </span>
                          )}
                      </div>

                      {renderCompactScorecard(row.holeScores)}

                      {expandedMiniGameWins.length > 0 && (
                        <div className="mt-3 border-t border-[#e4ded1] pt-3">
                          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#68716c]">
                            Vinster i sidotävlingar
                          </p>
                          <div className="mt-1.5 space-y-1.5">
                            {expandedMiniGameWins.map((win) => (
                              <div
                                key={win.id}
                                className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-5 text-[#68716c]"
                              >
                                <StarIcon className="h-3.5 w-3.5 shrink-0 fill-[#b08b45] text-[#b08b45]" />
                                {row.unit_type === "team" && (
                                  <>
                                    <span className="font-semibold text-[#1f2d26]">
                                      {win.winnerName}
                                    </span>
                                    <span aria-hidden="true">·</span>
                                  </>
                                )}
                                <span>{win.gameName}</span>
                                <span aria-hidden="true">·</span>
                                <span>
                                  Hål {win.holeNumber}
                                </span>
                                {win.deduction > 0 && (
                                  <>
                                    <span aria-hidden="true">·</span>
                                    <span className="font-semibold text-[#287148]">
                                      −{win.deduction} slag
                                    </span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-[#68716c]">
                        <span className="rounded-full border border-[#d8d2c4] bg-[#f7f4ec] px-2.5 py-1">
                          Spelat {row.holesPlayed}/{holes}
                        </span>
                        <span className="rounded-full border border-[#d8d2c4] bg-[#f7f4ec] px-2.5 py-1">
                          Brutto {row.gross}
                        </span>
                        {row.handicapDeduction > 0 && (
                          <span className="rounded-full border border-[#c9ddcf] bg-[#eef6f0] px-2.5 py-1 text-[#287148]">
                            HCP −{row.handicapDeduction}
                          </span>
                        )}
                        {row.miniGameDeduction > 0 &&
                          row.unit_type !== "team" && (
                            <span className="rounded-full border border-[#dfcfab] bg-[#fbf4e3] px-2.5 py-1 text-[#8b692b]">
                              {includeMiniGamesInRanking
                                ? `Sidotävlingar −${row.miniGameDeduction}`
                                : `★ −${row.miniGameDeduction} ej inräknat`}
                            </span>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {leaderboardRows.rows.length === 0 && (
            <div className="px-4 py-10 text-center">
              <GaugeIcon className="mx-auto h-6 w-6 text-[#9a7735]" />
              <p className="mt-3 text-sm font-semibold text-[#183d2d]">
                Inga resultat ännu
              </p>
              <p className="mt-1 text-xs text-[#68716c]">
                Ställningen visas när den första bollen börjar registrera scorer.
              </p>
            </div>
          )}
        </section>

        {teamLeaderboardRows.length > 0 && (
          <section className="mt-4 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
            <div className="flex items-end justify-between gap-3 border-b border-[#d8d2c4] bg-[#f7f4ec] px-4 py-3.5 sm:px-5">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#68716c]">
                  Lagtävling
                </p>
                <h3 className="mt-0.5 text-sm font-semibold text-[#183d2d]">
                  Lagets total
                </h3>
              </div>
              <span className="shrink-0 text-[8px] font-medium uppercase tracking-[0.1em] text-[#68716c]">
                {leaderboardScope === "total"
                  ? "Totalt inkl. live"
                  : "Pågående runda"}
              </span>
            </div>

            <div className="grid gap-2 p-3 sm:grid-cols-2 sm:p-4">
              {teamLeaderboardRows.map((team, teamIndex) => (
                <div
                  key={team.key}
                  className="rounded-xl border border-[#d8d2c4] border-l-4 bg-[#fffdf8] px-3.5 py-3"
                  style={{ borderLeftColor: team.color }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#68716c]">
                        Placering {teamIndex + 1}
                      </p>
                      <p className="mt-1 truncate text-sm font-semibold text-[#1f2d26]">
                        Team {team.name}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[9px] text-[#68716c]">
                        <span>
                          {team.playerCount} spelare
                        </span>
                        <span>
                          {team.holesPlayed}/{holes} hål
                        </span>
                        {team.miniGameDeduction > 0 && (
                          <span className="font-semibold text-[#9a7735]">
                            ★ −{team.miniGameDeduction}
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="shrink-0 text-xl font-semibold leading-none tabular-nums text-[#183d2d]">
                      {leaderboardScope === "total"
                        ? team.total
                        : team.roundTotal}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {miniGameResults.length > 0 && (
          <section className="mt-4 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
            <div className="flex items-center gap-3 border-b border-[#d8d2c4] bg-[#f7f4ec] px-4 py-3.5 sm:px-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#fbf4e3] text-[#9a7735]">
                <TargetIcon className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#68716c]">
                  Sidotävlingar
                </p>
                <h3 className="mt-0.5 text-sm font-semibold text-[#183d2d]">
                  Resultat och vinnare
                </h3>
              </div>
            </div>

            <div className="grid gap-2 p-3 sm:grid-cols-2 sm:p-4">
              {miniGameResults.map((result) => (
                <div
                  key={result.id}
                  className="rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-3.5 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[#1f2d26]">
                        {result.mini_game_name || "Sidotävling"}
                      </p>
                      <p className="mt-1 text-[10px] text-[#68716c]">
                        Hål {result.hole_number}
                      </p>
                    </div>
                    <StarIcon className="h-4 w-4 shrink-0 fill-[#b08b45] text-[#b08b45]" />
                  </div>

                  {result.award ? (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-[#183d2d]">
                        {result.winnerName}
                      </p>
                      {result.award.distance_meters != null && (
                        <p className="mt-1 text-[10px] text-[#68716c]">
                          {formatDistance(
                            result.award.distance_meters
                          )}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-[#8a8f8b]">
                      Ingen vinnare fastställd ännu.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-4 flex items-center justify-center gap-2 text-[10px] text-[#8a8f8b]">
          <EyeIcon className="h-3.5 w-3.5" />
          Skrivskyddad livevisning – resultaten kan inte ändras här.
        </div>
      </main>
    </div>
  );
}